import { useAppForm } from "@features/form/useAppForm";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { revalidateLogic } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { toast } from "sonner";
import z from "zod";
import { authClient } from "./authClient";
import { AuthRoute } from "./AuthRoute";

export const ResetPasswordPage = () => {
	const { token } = AuthRoute.useSearch();
	const navigate = useNavigate();
	const LL = useI18nContext().LL.auth;

	const ResetPasswordSchema = useMemo(
		() =>
			z.object({
				password: z.string().min(8, LL.FORM_FIELDS_PASSWORD_ERROR_MESSAGE()),
			}),
		[LL]
	);
	const form = useAppForm({
		defaultValues: {
			password: "",
		},
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: ResetPasswordSchema,
		},
		onSubmit: async ({ value }) => {
			try {
				const res = await authClient.resetPassword({
					token,
					newPassword: value.password,
				});
				if (res.error) {
					throw res.error;
				}
				navigate({ to: AuthRoute.fullPath, search: { token: undefined } });
			} catch (error: any) {
				console.error(error);
				toast.error("message" in error ? (error.message as string) : "Something went wrong, try again later");
			}
		},
	});

	return (
		<div className="m-auto w-full max-w-sm space-y-6">
			<div className="space-y-2 text-center">
				<h1 className="text-3xl font-bold font-breit">
					<T>{LL.RESET_PASSWORD_TITLE()}</T>
				</h1>
				<p className="text-muted-foreground text-sm">
					<T>{LL.RESET_PASSWORD_DESCRIPTION()}</T>
				</p>
			</div>

			<form
				id="reset-password"
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<form.AppField name="password">
					{(field) => (
						<field.Password
							label={LL.FORM_FIELDS_PASSWORD_LABEL()}
							placeholder={LL.FORM_FIELDS_PASSWORD_PLACEHOLDER()}
							autoComplete="new-password"
							description={LL.FORM_FIELDS_PASSWORD_DESCRIPTION()}
						/>
					)}
				</form.AppField>
			</form>

			<div className="flex flex-col space-y-4">
				<form.AppForm>
					<form.SubmitButton className="w-full" form="reset-password">
						<T>{LL.RESET_PASSWORD_SUBMIT_BUTTON()}</T>
					</form.SubmitButton>
				</form.AppForm>
			</div>
		</div>
	);
};
